import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import * as Sentry from "@sentry/react";

import {
  useTheme,
  Grid,
  Typography,
  Button,
  IconButton,
  Chip,
  Box,
  TextField,
  Tooltip,
  CircularProgress,
  ThemeProvider,
  Dialog
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { Autocomplete } from "@aclymatepackages/atoms";
import { MultiPartFormLayout } from "@aclymatepackages/multi-part-form";
import mainTheme, { mergeDarkTheme } from "@aclymatepackages/themes";
import { calculateMergedProductFootprint } from "@aclymatepackages/products";

import Backdrop from "../../atoms/mui/Backdrop";
import ReportRenderer from "../../platform-pages/dashboard/views/customers/ReportRenderer";
import ProductPdfCapture from "../../platform-pages/dashboard/views/customers/ProductPdfCapture";

import {
  useAccountData,
  useCachedDisplayData,
  useCachedFirebaseCrud,
  useMutateCachedCollection
} from "../../../helpers/firebase";
import { useUpdateVendorRelationshipFields } from "../../../helpers/components/vendors";
import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import { getAccountId } from "../../../helpers/accountHelpers";
import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";
import {
  isLocalOnlyMode,
  isProductOwnedBy
} from "../../../helpers/components/products";
import useEmissionsContext from "../../../helpers/contexts/emissions";
import useProductsEmissionsFactors from "../../../helpers/contexts/ProductsEmissionsFactors";

const CompanySearchInput = ({ onSearchComplete }) => {
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [searching, setSearching] = useState(false);

  const handleSearch = () => {
    setSearching(true);
    onSearchComplete({ name, description });
  };

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <TextField
          label="What does your customer do?"
          size="small"
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Grid>
      <Grid item>
        <TextField
          label="What is your customer's company name?"
          size="small"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          helperText="Use the full company name (e.g. United Airlines instead of United)."
        />
      </Grid>
      <Grid item container justifyContent="center">
        <Grid item>
          <Button
            variant="contained"
            disabled={!name || !description || searching}
            onClick={handleSearch}
          >
            Search
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

CompanySearchInput.propTypes = {
  onSearchComplete: PropTypes.func.isRequired
};

const dateValuePropType = PropTypes.oneOfType([
  PropTypes.instanceOf(Date),
  PropTypes.string,
  PropTypes.number,
  PropTypes.shape({
    seconds: PropTypes.number,
    _seconds: PropTypes.number
  })
]);

const emptyContact = () => ({ id: crypto.randomUUID(), name: "", email: "" });

const buildAvailableYears = (startDate) => {
  const startYear = dayjs(startDate).year();
  const currentYear = dayjs().year();
  return Array.from(
    { length: currentYear - startYear },
    (_, i) => startYear + i
  );
};

const ContactsInput = ({ contacts, setContacts }) => {
  const addContact = () => setContacts((prev) => [...prev, emptyContact()]);

  const removeContact = (id) =>
    setContacts((prev) => prev.filter((c) => c.id !== id));

  const updateContact = (id, field, value) =>
    setContacts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );

  return (
    <Grid container direction="column" spacing={2}>
      {contacts.map((contact) => (
        <Grid item key={contact.id}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={5}>
              <TextField
                label="Name"
                size="small"
                fullWidth
                value={contact.name}
                onChange={(e) =>
                  updateContact(contact.id, "name", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label="Email"
                size="small"
                fullWidth
                value={contact.email}
                onChange={(e) =>
                  updateContact(contact.id, "email", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              {contacts.length > 1 && (
                <IconButton
                  size="small"
                  onClick={() => removeContact(contact.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </Grid>
      ))}
      <Grid item container justifyContent="center">
        <Grid item>
          <Button
            startIcon={<AddIcon />}
            size="small"
            color="primary"
            onClick={addContact}
          >
            Add another contact
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

ContactsInput.propTypes = {
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      email: PropTypes.string
    })
  ).isRequired,
  setContacts: PropTypes.func.isRequired
};

const YearSelectionInput = ({
  selectedYears,
  setSelectedYears,
  accountingYears,
  startDate
}) => {
  const availableYears = startDate ? buildAvailableYears(startDate) : [];
  const lockedYears = (accountingYears || []).map(({ date }) =>
    dayjs(date).year()
  );

  const toggleYear = (year) =>
    setSelectedYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    );

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item>
        <Typography variant="body2" color="textSecondary">
          Select which completed years to share with this customer. The current
          year becomes available for sharing once it ends. You can always add
          more from the customer detail view.
        </Typography>
      </Grid>
      <Grid item container spacing={1} justifyContent="center">
        <ThemeProvider theme={mainTheme}>
          {availableYears.map((year) => {
            const isFinalized = lockedYears.includes(year);
            const isSelected = selectedYears.includes(year);
            return (
              <Grid item key={year}>
                <Tooltip title={isFinalized ? "Finalized" : "Preliminary"}>
                  <Chip
                    label={year}
                    color={isFinalized ? "secondary" : "warning"}
                    variant={isSelected ? "filled" : "outlined"}
                    onClick={() => toggleYear(year)}
                  />
                </Tooltip>
              </Grid>
            );
          })}
        </ThemeProvider>
        {availableYears.length === 0 && (
          <Grid item>
            <Typography variant="caption" color="textSecondary">
              No report years available yet.
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

YearSelectionInput.propTypes = {
  selectedYears: PropTypes.arrayOf(PropTypes.number).isRequired,
  setSelectedYears: PropTypes.func.isRequired,
  accountingYears: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      date: dateValuePropType
    })
  ),
  startDate: dateValuePropType
};

const ProductSelectionInput = ({
  selectedProductIds,
  setSelectedProductIds,
  dbProducts,
  productLifecycleEvents
}) => {
  const options = (dbProducts || []).map(({ id, name }) => ({ id, name }));

  const selectedOptions = options.filter((o) =>
    selectedProductIds.includes(o.id)
  );

  const isFinalized = (productId) => {
    const event = (productLifecycleEvents || []).find(
      (e) => e.productId === productId
    );
    return event?.bomStatus === "confirmed";
  };

  const renderOption = (props, option) => (
    <li {...props} key={option.id}>
      <Box display="flex" alignItems="center" gap={1}>
        {option.name}
        <ThemeProvider theme={mainTheme}>
          <Chip
            label={isFinalized(option.id) ? "Finalized" : "Preliminary"}
            color={isFinalized(option.id) ? "secondary" : "warning"}
            size="small"
          />
        </ThemeProvider>
      </Box>
    </li>
  );

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item>
        <Typography variant="body2" color="textSecondary">
          Select which products to share with this customer.
        </Typography>
      </Grid>
      <Grid item>
        <Autocomplete
          label="Select products"
          availableOptions={options}
          multiple
          value={selectedOptions}
          setValue={(newOptions) =>
            setSelectedProductIds((newOptions || []).map((o) => o.id))
          }
          onDelete={(removed) =>
            setSelectedProductIds((prev) =>
              prev.filter((id) => id !== removed.id)
            )
          }
          renderOption={renderOption}
          disableCloseOnSelect
          size="small"
        />
      </Grid>
    </Grid>
  );
};

ProductSelectionInput.propTypes = {
  selectedProductIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedProductIds: PropTypes.func.isRequired,
  dbProducts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })
  ),
  productLifecycleEvents: PropTypes.arrayOf(
    PropTypes.shape({
      productId: PropTypes.string,
      bomStatus: PropTypes.string
    })
  )
};

const NewCustomerForm = ({ onClose }) => {
  const { palette } = useTheme();
  const mutateCachedCollection = useMutateCachedCollection();
  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const layoutContext = usePlatformLayoutContext() || {};
  const { activateSnackbar } = layoutContext;
  const updateVendorRelationshipFields =
    useUpdateVendorRelationshipFields("customers");
  const [{ name: vendorName, id: vendorId, startDate }] = useAccountData();
  const [accountingYears] = useCachedDisplayData("accounting");
  const [dbProducts] = useCachedDisplayData("product-components");
  const [productLifecycleEvents] = useCachedDisplayData(
    "product-lifecycle-events"
  );
  const { allEmissions } = useEmissionsContext();
  const [{ emissionsFactors }] = useProductsEmissionsFactors();
  const [matchedCompany, setMatchedCompany] = useState(null);
  const [pendingMerge, setPendingMerge] = useState(null);
  const [pendingCompanySearchInput, setPendingCompanySearchInput] =
    useState(null);
  const [contacts, setContacts] = useState([emptyContact()]);
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [generatingYears, setGeneratingYears] = useState([]);
  const [generatingProductIds, setGeneratingProductIds] = useState([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [totalReportCount, setTotalReportCount] = useState(0);
  const pendingSharedReportsRef = useRef([]);
  const pendingSharedProductsRef = useRef([]);
  const pendingRelationshipIdRef = useRef(null);
  const pendingBuyerIdRef = useRef(null);
  const relationshipWriteQueueRef = useRef(Promise.resolve());

  useEffect(() => {
    if (totalReportCount === 0 || completedCount < totalReportCount) return;
    onClose();
  }, [completedCount, totalReportCount, onClose]);

  // Report uploads finish independently and each one writes the whole
  // sharedReports/sharedProducts arrays. Queueing the writes, each reading the
  // refs when it actually runs, keeps an earlier write from landing last and
  // wiping out a later upload's share -- which, for products, is also what
  // sync-product-buyer-access derives the buyer's access from.
  const queueRelationshipWrite = () => {
    const write = relationshipWriteQueueRef.current.then(() =>
      updateVendorRelationshipFields(pendingRelationshipIdRef.current, {
        sharedReports: pendingSharedReportsRef.current,
        sharedProducts: pendingSharedProductsRef.current
      })
    );
    relationshipWriteQueueRef.current = write.catch(() => undefined);
    return write;
  };

  const onReportUploaded = async (year, url) => {
    const now = new Date();
    pendingSharedReportsRef.current = pendingSharedReportsRef.current.map(
      (r) => (r.year === year ? { ...r, generatedAt: now, reportUrl: url } : r)
    );
    const isFinalized = lockedYears.includes(year);
    const accountingDoc = (accountingYears || []).find(
      ({ date }) => dayjs(date).year() === year
    );
    const canonicalWrites =
      isFinalized && accountingDoc?.id
        ? [
            updateCollectionDoc("accounting", accountingDoc.id, {
              reportUrl: url,
              reportGeneratedAt: now
            })
          ]
        : [];
    if (!isFinalized) {
      sessionStorage.setItem(`preliminary-report-url-${vendorId}-${year}`, url);
    }
    await Promise.all([queueRelationshipWrite(), ...canonicalWrites]);
    setCompletedCount((prev) => prev + 1);
  };

  const onReportError = () => setCompletedCount((prev) => prev + 1);

  const requestBuyerAccessSync = (productId, buyerId) =>
    fetchOurApi({
      path: "/products/sync-product-buyer-access",
      method: "POST",
      data: { productId, buyerId }
    });

  // Runs after the relationship write: the backend derives the buyer's read
  // access to the product from that saved share state. fetchOurApi resolves
  // with { error } rather than throwing, so a failure is retried once (the
  // sync is idempotent), then reported and surfaced here.
  const syncReportRecipientAccess = async (productId, buyerId, productName) => {
    const firstAttempt = await requestBuyerAccessSync(productId, buyerId);
    const result = firstAttempt?.error
      ? await requestBuyerAccessSync(productId, buyerId)
      : firstAttempt;
    if (!result?.error) {
      return;
    }
    Sentry.captureException(new Error("Product buyer access sync failed"), {
      tags: { feature: "product-buyer-access-sync" },
      extra: { productId, message: result.message }
    });
    activateSnackbar?.({
      message: `The report for ${productName} was shared, but we couldn't give this customer access to the product. Toggle its report off and on from the customer's Products tab to retry.`,
      severity: "error"
    });
  };

  const onProductReportUploaded = async (productId, url) => {
    pendingSharedProductsRef.current = pendingSharedProductsRef.current.map(
      (p) =>
        p.productId === productId
          ? { ...p, reportUrl: url, reportShared: true }
          : p
    );
    const buyerId = pendingBuyerIdRef.current;
    const productName =
      pendingSharedProductsRef.current.find((p) => p.productId === productId)
        ?.productName || "this product";
    const relationshipWrite = await queueRelationshipWrite();
    if (relationshipWrite?.error) {
      activateSnackbar?.({
        message: `We couldn't save the report share for ${productName}. Share it again from the customer's Products tab.`,
        severity: "error"
      });
      setCompletedCount((prev) => prev + 1);
      return;
    }
    if (buyerId && !isLocalOnlyMode(layoutContext)) {
      await syncReportRecipientAccess(productId, buyerId, productName);
    }
    setCompletedCount((prev) => prev + 1);
  };

  const onProductReportError = () => setCompletedCount((prev) => prev + 1);

  const hasValidContacts =
    contacts.length > 0 && contacts.every((c) => c.name && c.email);

  const lockedYears = (accountingYears || []).map(({ date }) =>
    dayjs(date).year()
  );

  // fetchCompanyDataFromName used to always create a new v3-companies doc the
  // moment its exact-name check missed -- before the user ever confirmed or
  // even finished this form, and regardless of whether a fuzzy-matching
  // existing company already existed. It now takes checkForDuplicates: true
  // to fall back to a fuzzy match first; called directly via fetchOurApi
  // (bypassing the shared fetchCompanyDataFromName JS helper, which discards
  // everything but companyData) so this can see requiresConfirmation.
  const applyCompanySearchResponse = (response, { name }) => {
    if (response?.requiresConfirmation) {
      setPendingMerge({
        matchedCompanyId: response.matchedCompanyId,
        matchedCompanyName: response.matchedCompanyName,
        source: "search"
      });
      return;
    }

    setPendingMerge(null);
    setMatchedCompany(response?.companyData || { name });
  };

  const onCompanySearch = async ({ name, description }) => {
    setPendingCompanySearchInput({ name, description });

    const response = await fetchOurApi({
      path: "/companies/fetch-company-data-from-name",
      method: "POST",
      data: {
        name,
        description,
        accountId: getAccountId(),
        checkForDuplicates: true
      },
      callback: (response) => response
    });

    applyCompanySearchResponse(response, { name });
  };

  const onCompanySearchMergeDecision = async (mergeDecision) => {
    const { name, description } = pendingCompanySearchInput || {};

    const response = await fetchOurApi({
      path: "/companies/fetch-company-data-from-name",
      method: "POST",
      data: {
        name,
        description,
        accountId: getAccountId(),
        checkForDuplicates: true,
        mergeDecision,
        ...(mergeDecision === "merge" && pendingMerge
          ? { confirmedMatchedCompanyId: pendingMerge.matchedCompanyId }
          : {})
      },
      callback: (response) => response
    });

    applyCompanySearchResponse(response, { name });
  };

  // Separated from the rest of onFormSubmit's work so a candidate-tier match
  // can pause here for the user's yes/no, then resume with the exact same
  // downstream logic once resolved -- create-customer-relationship's match is
  // always re-derived server-side, never trusted from the client alone, so
  // resubmitting with mergeDecision + confirmedMatchedCompanyId is safe.
  const submitCustomerRelationship = async (mergeDecision) => {
    const response = await fetchOurApi({
      path: "/vendors/create-customer-relationship",
      method: "POST",
      data: {
        vendorId,
        vendorName,
        ...(matchedCompany?.id ? { buyerId: matchedCompany.id } : {}),
        buyerName: matchedCompany?.name || "",
        customerContacts: contacts.map(({ id, name, email }) => ({
          id,
          name,
          email,
          explorerInvitedAt: null
        })),
        ...(mergeDecision ? { mergeDecision } : {}),
        ...(mergeDecision === "merge" && pendingMerge
          ? { confirmedMatchedCompanyId: pendingMerge.matchedCompanyId }
          : {})
      },
      callback: (response) => response
    });

    if (response?.requiresConfirmation) {
      setPendingMerge({
        matchedCompanyId: response.matchedCompanyId,
        matchedCompanyName: response.matchedCompanyName,
        source: "submit"
      });
      return null;
    }

    setPendingMerge(null);
    return response;
  };

  const finishCustomerCreation = async ({ relationshipId, buyerId: resolvedBuyerId }) => {
    // The server may have resolved a different (existing) company than the
    // one the search step guessed -- keep matchedCompany in sync so the
    // product-sharing step below and any later invite copy uses the real,
    // final company rather than the pre-submit guess.
    if (resolvedBuyerId && resolvedBuyerId !== matchedCompany?.id) {
      setMatchedCompany((prev) => ({ ...prev, id: resolvedBuyerId }));
    }

    const now = new Date();

    const findCanonicalReportUrl = (year) => {
      const isFinalized = lockedYears.includes(year);
      if (isFinalized) {
        const accountingDoc = (accountingYears || []).find(
          ({ date }) => dayjs(date).year() === year
        );
        return accountingDoc?.reportUrl || null;
      }
      return (
        sessionStorage.getItem(`preliminary-report-url-${vendorId}-${year}`) ||
        null
      );
    };

    const sharedReports = selectedYears.map((year) => {
      const canonicalUrl = findCanonicalReportUrl(year);
      return {
        year,
        sharedAt: now,
        generatedAt: canonicalUrl ? now : null,
        status: "shared",
        reportUrl: canonicalUrl,
        isFinalized: lockedYears.includes(year)
      };
    });

    const yearsNeedingGeneration = selectedYears.filter(
      (year) => !findCanonicalReportUrl(year)
    );

    const sharedProducts = selectedProductIds.map((productId) => {
      const event = (productLifecycleEvents || []).find(
        (e) => e.productId === productId
      );
      const productDoc = (dbProducts || []).find((p) => p.id === productId);
      return {
        productId,
        productName: productDoc?.name || "",
        sharedAt: now,
        reportUrl: null,
        bomShared: false,
        reportShared: false,
        bomStatus: "shared",
        reportStatus: "shared",
        isFinalized: event?.bomStatus === "confirmed"
      };
    });

    await Promise.all(
      contacts.map(({ name: recipientName, email }) =>
        fetchOurApi({
          path: "/vendors/invite-customer-to-explorer",
          method: "POST",
          data: {
            vendorId,
            relationshipId,
            recipientName,
            email,
            companyName: matchedCompany?.name || "",
            senderName: vendorName
          }
        })
      )
    );

    if (resolvedBuyerId && selectedProductIds.length > 0) {
      await Promise.all(
        sharedProducts.map(({ productId, isFinalized }) =>
          fetchOurApi({
            path: "/products/share-product-with-buyer",
            method: "POST",
            data: {
              productId,
              vendorId,
              buyerId: resolvedBuyerId,
              bomStatus: isFinalized ? "confirmed" : "shared"
            }
          })
        )
      );
    }

    const invitedContacts = contacts.map(({ id, name, email }) => ({
      id,
      name,
      email,
      explorerInvitedAt: now
    }));

    await updateVendorRelationshipFields(relationshipId, {
      customerContacts: invitedContacts,
      ...(sharedReports.length > 0 ? { sharedReports } : {}),
      ...(sharedProducts.length > 0 ? { sharedProducts } : {})
    });

    await mutateCachedCollection("customers");

    const total = yearsNeedingGeneration.length + selectedProductIds.length;

    if (total === 0) {
      onClose();
      return;
    }

    pendingRelationshipIdRef.current = relationshipId;
    pendingBuyerIdRef.current = resolvedBuyerId || null;
    pendingSharedReportsRef.current = sharedReports;
    pendingSharedProductsRef.current = sharedProducts;
    setTotalReportCount(total);
    setGeneratingYears(yearsNeedingGeneration);
    setGeneratingProductIds(selectedProductIds);
  };

  const onFormSubmit = async () => {
    const response = await submitCustomerRelationship();
    if (!response) return; // paused on a candidate-tier match -- see pendingMerge UI
    await finishCustomerCreation(response);
  };

  const onMergeDecision = async (mergeDecision) => {
    const response = await submitCustomerRelationship(mergeDecision);
    if (!response) return;
    await finishCustomerCreation(response);
  };

  const topLevelProducts = (dbProducts || []).filter(
    (p) => !p.parentProductId
  );

  const productsWithFootprint = topLevelProducts.filter((product) => {
    const { id } = product;
    if (!isProductOwnedBy(product, vendorId)) {
      return false;
    }
    const event = (productLifecycleEvents || []).find(
      (e) => e.productId === id
    );
    return !event?.bomStatus || event?.bomStatus === "confirmed";
  });

  const forms = [
    {
      name: "company",
      label: "Find Company",
      title: "Find Your Customer's Company",
      size: "lg"
    },
    {
      name: "contacts",
      label: "Add Contacts",
      title: "Add Customer Contacts",
      size: "lg"
    },
    {
      name: "reports",
      label: "Annual Reports",
      title: "Select Annual Reports to Share",
      size: "lg"
    },
    ...(productsWithFootprint.length > 0
      ? [
          {
            name: "products",
            label: "Products",
            title: "Select Products to Share",
            size: "lg"
          }
        ]
      : [])
  ];

  const rows = [
    {
      label: "Find customer's company",
      value: !!matchedCompany,
      NextStepInput: CompanySearchInput,
      nextStepInputProps: {
        onInputSelect: onCompanySearch,
        inputSelectPropName: "onSearchComplete"
      },
      form: "company"
    },
    {
      label: "Add at least one contact",
      value: hasValidContacts,
      input: <ContactsInput contacts={contacts} setContacts={setContacts} />,
      form: "contacts"
    },
    {
      label: "Select years to share",
      value: true,
      input: (
        <YearSelectionInput
          selectedYears={selectedYears}
          setSelectedYears={setSelectedYears}
          accountingYears={accountingYears}
          startDate={startDate}
        />
      ),
      form: "reports"
    },
    ...(productsWithFootprint.length > 0
      ? [
          {
            label: "Select products to share",
            value: true,
            input: (
              <ProductSelectionInput
                selectedProductIds={selectedProductIds}
                setSelectedProductIds={setSelectedProductIds}
                dbProducts={productsWithFootprint}
                productLifecycleEvents={productLifecycleEvents}
              />
            ),
            form: "products"
          }
        ]
      : [])
  ];

  const isGenerating = totalReportCount > 0;

  const generatingMergedProducts = generatingProductIds
    .map((productId) => {
      const productDoc = (dbProducts || []).find((p) => p.id === productId);
      if (!productDoc) return null;
      const event =
        (productLifecycleEvents || []).find((e) => e.productId === productId) ||
        {};
      return {
        productId,
        product: calculateMergedProductFootprint(
          productDoc,
          event,
          emissionsFactors
        ),
        isFinalized: event.bomStatus === "confirmed"
      };
    })
    .filter(Boolean);

  return (
    <Backdrop
      style={{ overflowY: "auto" }}
      boxStyle={{ width: "768px", overflowY: "auto", mb: 4 }}
      onClose={onClose}
    >
      {generatingYears.map((year) => (
        <ReportRenderer
          key={year}
          year={year}
          emissions={allEmissions}
          isFinalized={lockedYears.includes(year)}
          onUploaded={(url) => onReportUploaded(year, url)}
          onError={onReportError}
        />
      ))}
      {generatingMergedProducts.map(({ productId, product, isFinalized }) => (
        <ProductPdfCapture
          key={productId}
          product={product}
          isFinalized={isFinalized}
          onUploaded={(url) => onProductReportUploaded(productId, url)}
          onError={onProductReportError}
        />
      ))}
      {/* Rendered as a Dialog overlay, not swapped in for MultiPartFormLayout,
          so the form stays mounted underneath -- MultiPartFormLayout tracks
          step progress in its own internal state, and unmounting it (as an
          earlier version of this did) reset that progress to step 0 on
          remount once the user answered, sending them back to "Find Company"
          instead of continuing from wherever they were. */}
      <Dialog open={!!pendingMerge} maxWidth="sm" fullWidth>
        {pendingMerge && (
          <ThemeProvider theme={mergeDarkTheme}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              p={4}
              gap={2}
            >
              <Typography variant="h6" color="textPrimary" align="center">
                We found an existing company that looks like a match
              </Typography>
              <Typography variant="body1" color="textPrimary" align="center">
                Is &quot;{pendingMerge.matchedCompanyName}&quot; the same company as{" "}
                {matchedCompany?.name || "the one you searched for"}?
              </Typography>
              <Box display="flex" gap={2} mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    pendingMerge.source === "search"
                      ? onCompanySearchMergeDecision("merge")
                      : onMergeDecision("merge")
                  }
                >
                  Yes, this is the same company
                </Button>
                <Button
                  variant="outlined"
                  onClick={() =>
                    pendingMerge.source === "search"
                      ? onCompanySearchMergeDecision("new")
                      : onMergeDecision("new")
                  }
                >
                  No, create a new company
                </Button>
              </Box>
            </Box>
          </ThemeProvider>
        )}
      </Dialog>
      {isGenerating ? (
        <ThemeProvider theme={mergeDarkTheme}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            style={{ height: "100vh" }}
            gap={2}
          >
            <CircularProgress />
            <Typography variant="body2" color="textPrimary">
              Generating reports... ({completedCount} of {totalReportCount})
            </Typography>
          </Box>
        </ThemeProvider>
      ) : (
        <MultiPartFormLayout
          color={palette.primary.main}
          title="Add a Customer"
          subtitle="Share your emissions data with your customers"
          onFormSubmit={onFormSubmit}
          forms={forms}
          rows={rows}
          submitLoadingText="Creating customer..."
          formSubmitText="Add Customer"
          theme={mergeDarkTheme}
          buttonTheme={mainTheme}
          allowFormScrolling
        />
      )}
    </Backdrop>
  );
};

NewCustomerForm.propTypes = {
  onClose: PropTypes.func.isRequired
};

export default NewCustomerForm;
